import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  InputAdornment,
  Icon,
  LinearProgress,
  Divider,
  IconButton,
  Grid,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import RequiredLabel from "components/CommonComponent/RequiredLabel";
import { useNotify } from "components/CommonComponent/NotificationProvider";
import { useParams, useNavigate } from "react-router-dom";
import { validateUUID } from "actions/password";
import { resetPasswordAPI } from "actions/password";
import BasicLayout from "pages/authentication/components/BasicLayout";
import Card from "@mui/material/Card";

const initialState = {
  newPassword: { value: "", error: "" },
  confirmPassword: { value: "", error: "" },
};

function ResetPassword() {
  const { uuid } = useParams();
  const { notify } = useNotify();
  const navigate = useNavigate();
  const [passwordForm, setPasswordForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [resetStatus, setResetStatus] = useState(null);
  const [show, setShow] = useState({
    new: false,
    confirm: false,
  });

  //get uuid for validate uuid
  useEffect(() => {
    getValidationUUID(uuid);
  }, [uuid]);

  //validate
  const getValidationUUID = async () => {
    try {
      const res = await validateUUID(uuid);
      if (res?.code === 200) {
        setResetStatus(null);
      } else {
        setResetStatus("error"); // invalid/expired link
      }
    } catch (err) {
      setResetStatus("error"); // invalid/expired link
    }
  };

  const getPasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (password.length >= 12) score++;
    score = Math.min(score, 5);

    if (score <= 2) return { score, label: "Weak" };
    if (score <= 4) return { score, label: "Medium" };
    return { score, label: "Strong" };
  };

  const getStrengthValue = (strength) => {
    if (strength?.label === "Strong") return 100;
    if (strength?.label === "Medium") return 60;
    return 30;
  };

  const getStrengthColor = (strength) => {
    if (strength?.label === "Strong") return "success";
    if (strength?.label === "Medium") return "warning";
    return "error";
  };

  const toggleVisibility = (key) =>
    setShow((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleChange = (name, value) => {
    setPasswordForm((prev) => ({
      ...prev,
      [name]: { value, error: "" },
    }));
  };

  const getValidationError = (name, value) => {
    switch (name) {
      case "currentPassword":
        return !value ? "Please enter current password" : "";
      case "newPassword":
        if (!value) return "Please enter a new password";
        if (value.length < 8)
          return "Password must contain at least 8 characters";
        if (
          passwordForm.confirmPassword.value &&
          value !== passwordForm.confirmPassword.value
        )
          return "The new password and confirm password must match";
        return "";
      case "confirmPassword":
        if (!value) return "Please confirm password";
        if (value !== passwordForm.newPassword.value)
          return "The new password and confirm password must match";
        return "";
      default:
        return "";
    }
  };

  const handleOnBlur = (name, value) => {
    const error = getValidationError(name, value);

    setPasswordForm((prev) => ({
      ...prev,
      [name]: { ...prev[name], error },
    }));
  };

  const validateForm = () => {
    let temp = { ...passwordForm };
    Object.keys(temp).forEach((key) => {
      temp[key] = {
        ...temp[key],
        error: getValidationError(key, temp[key].value),
      };
    });
    setPasswordForm(temp);
    return Object.values(temp).every((f) => f.error === "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    try {
      const payload = {
        uuid: uuid,
        password: passwordForm.newPassword.value,
      };

      const res = await resetPasswordAPI(payload);
      if (res?.code === 200) {
        notify("Password reset successfully", "success");
        navigate("/auth/sign-in");
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Reset failed";
        notify(message, "error");
      }
    } catch (error) {
      notify(error?.message || "Reset failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const strength = getPasswordStrength(passwordForm.newPassword.value);

  const canCloseDialog = () => {
    if (auth?.isPasswordSet === "NO") {
      notify("Please set your password first", "info");
      return false;
    }
    return true;
  };
  return (
    <BasicLayout>
      <MDBox
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Card sx={{ maxWidth: 500, width: "100%", p: 3 }}>
          <MDBox textAlign="center" mb={3}>
            <MDTypography variant="h5">Reset Password</MDTypography>
            <MDTypography variant="caption" color="text">
              Choose a strong password to keep your account secure
            </MDTypography>
          </MDBox>

          <MDBox component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* New Password */}
              <Grid item xs={12}>
                <RequiredLabel required>New Password</RequiredLabel>
                <MDInput
                  variant="outlined"
                  placeholder="Please enter new password"
                  fullWidth
                  name="newPassword"
                  type={show.new ? "text" : "password"}
                  value={passwordForm.newPassword.value}
                  onChange={(e) => handleChange(e.target.name, e.target.value)}
                  onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                  error={!!passwordForm.newPassword.error}
                  inputProps={{ maxLength: 16 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => toggleVisibility("new")}
                          edge="end"
                        >
                          <Icon>
                            {show.new ? "visibility_off" : "visibility"}
                          </Icon>
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {passwordForm.newPassword.error && (
                  <MDTypography variant="caption" color="error">
                    {passwordForm.newPassword.error}
                  </MDTypography>
                )}

                {/* Strength bar */}
                {passwordForm.newPassword.value && (
                  <MDBox mt={1}>
                    <LinearProgress
                      variant="determinate"
                      value={getStrengthValue(strength)}
                      color={getStrengthColor(strength)}
                      sx={{ height: 6, borderRadius: 5 }}
                    />
                    <MDTypography
                      variant="caption"
                      color={getStrengthColor(strength)}
                    >
                      {strength.label} password
                    </MDTypography>
                  </MDBox>
                )}
              </Grid>

              {/* Confirm Password */}
              <Grid item xs={12}>
                <RequiredLabel required>Confirm Password</RequiredLabel>
                <MDInput
                  variant="outlined"
                  fullWidth
                  placeholder="Please confirm new password"
                  name="confirmPassword"
                  type={show.confirm ? "text" : "password"}
                  value={passwordForm.confirmPassword.value}
                  onChange={(e) => handleChange(e.target.name, e.target.value)}
                  onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                  error={!!passwordForm.confirmPassword.error}
                  inputProps={{ maxLength: 16 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => toggleVisibility("confirm")}
                          edge="end"
                        >
                          <Icon>
                            {show.confirm ? "visibility_off" : "visibility"}
                          </Icon>
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {passwordForm.confirmPassword.error && (
                  <MDTypography variant="caption" color="error">
                    {passwordForm.confirmPassword.error}
                  </MDTypography>
                )}
              </Grid>
            </Grid>

            <MDBox mt={3} display="flex" justifyContent="center">
              <MDButton
                variant="contained"
                color="deepBlue"
                type="submit"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <i className="fa fa-spinner fa-spin fa-lg text-white" />
                  </>
                ) : (
                  "Save"
                )}
              </MDButton>
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
    </BasicLayout>
  );
}

export default ResetPassword;
